import {Injectable} from '@angular/core';
import {Action, createSelector, State, StateContext} from '@ngxs/store';
import {patch} from '@ngxs/store/operators';
import {LoadFormatEvents} from '@store/format-events/format-events.actions';
import {pluckData} from '@tisa-group/cdk';
import {OttEventService} from '@tisa-group/core/api/umpire';
import {UmpireOttEvent} from '@tisa-group/models';
import {Observable, tap} from 'rxjs';

export interface PaginatedFormatEventsModel {
  items: UmpireOttEvent[];
  totalItems: number;
  currentPage: number;
}

const PAGE_SIZE = 16;

// used for events that don't belong to any segment
const NULL_SEGMENT_SYMBOL = Symbol('null segment');

// the model is formatSlug -> segmenKey -> value
export type FormatEventsStateModel = Record<
  string,
  Record<string | typeof NULL_SEGMENT_SYMBOL, PaginatedFormatEventsModel>
>;

@State<FormatEventsStateModel>({
  name: 'FormatEvent',
  defaults: {},
})
@Injectable()
export class FormatEventsState {
  @Action(LoadFormatEvents)
  LoadFormatEvents(
    ctx: StateContext<FormatEventsStateModel>,
    action: LoadFormatEvents,
  ): Observable<UmpireOttEvent[]> | UmpireOttEvent[] {
    const {formatSlug} = action;
    const segmentKey = action.segmentId || NULL_SEGMENT_SYMBOL;
    const formatState = ctx.getState()[formatSlug];
    const segmentState = formatState?.[segmentKey];

    // Pagination done, no more items to fetch
    if (
      segmentState?.totalItems &&
      segmentState?.items?.length >= segmentState.totalItems
    ) {
      return segmentState.items;
    }

    return this.eventService
      .list({
        include: ['image_horizontal'].join(','),
        filter: {
          format_slug: {
            rule: 'eq',
            value: formatSlug,
          },
          segment_id: action.segmentId
            ? {rule: 'eq', value: action.segmentId}
            : {rule: 'null', value: true},
        },
        sort: ['custom_events_sort'],
        page: {
          size: PAGE_SIZE,
          number: segmentState?.currentPage || 0,
        },
      })
      .pipe(
        tap(res =>
          ctx.setState(
            patch({
              [formatSlug]: {
                ...formatState,
                [segmentKey]: {
                  items: [...(segmentState?.items ?? []), ...res.data],
                  currentPage: res.meta.pagination.current + 1,
                  totalItems: res.meta.pagination.records,
                },
              },
            }),
          ),
        ),
        pluckData(),
      );
  }

  static getFormatEvents(
    formatSlug: string,
    segmentId: string | null,
  ): (state: FormatEventsStateModel) => PaginatedFormatEventsModel {
    return createSelector<(state: FormatEventsStateModel) => PaginatedFormatEventsModel>(
      [FormatEventsState],
      (state: FormatEventsStateModel) => {
        const segmentKey = segmentId || NULL_SEGMENT_SYMBOL;

        return state[formatSlug]?.[segmentKey];
      },
    );
  }

  constructor(readonly eventService: OttEventService) {}
}
